import { Box, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { ContainerCard } from "../../../../components/ContainerCard";
import { useLocalization } from "../../../../hooks/useLocalization";
import type { CountryUsageCardProps } from "../types";
import { exportCountryUsageCsv } from "../utils/exportCsv";
import { CountryUsageRow } from "./CountryUsageRow";
import { DataAnalyticsChartSkeleton } from "./DataAnalyticsChartSkeleton";

const COLLAPSED_ROW_COUNT = 4;

export const CountryUsageCard: React.FC<CountryUsageCardProps> = ({
  rows,
  title,
  barColor,
  isFetching = false,
  isLoading = false,
  isEmpty = false,
  isError = false,
  isUserLevelDataEnabled = false,
  startDate,
  endDate,
}) => {
  const { t } = useLocalization();
  const [expanded, setExpanded] = useState(false);

  const handleExportCsv = useCallback(() => {
    const safeTitle = title.toLowerCase().replaceAll(/\s+/g, "-");
    exportCountryUsageCsv(
      rows,
      isUserLevelDataEnabled,
      {
        country: t("pages.dataAnalytics.internalUsage.exportColumns.country"),
        value: t("pages.dataAnalytics.internalUsage.exportColumns.value"),
        percentage: t(
          "pages.dataAnalytics.internalUsage.exportColumns.percentage",
        ),
      },
      `${safeTitle}.csv`,
      { startDate, endDate },
      {
        startDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeStart",
        ),
        endDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeEnd",
        ),
      },
    );
  }, [rows, isUserLevelDataEnabled, title, t, startDate, endDate]);

  const canExpand = rows.length > COLLAPSED_ROW_COUNT;
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);

  if (isLoading) {
    return (
      <ContainerCard
        exportAction={{
          label: t("pages.dataAnalytics.internalUsage.exportCsv"),
          onClick: handleExportCsv,
          iconOnly: true,
        }}
        sx={{ flex: 1, width: "100%" }}
        title={title}
      >
        <DataAnalyticsChartSkeleton
          chartHeight={104}
          showFooterRow={false}
          showTopControls={false}
        />
      </ContainerCard>
    );
  }

  return (
    <ContainerCard
      exportAction={{
        label: t("pages.dataAnalytics.internalUsage.exportCsv"),
        onClick: handleExportCsv,
        iconOnly: true,
      }}
      isFetching={isFetching}
      sx={{ flex: 1, width: "100%" }}
      title={title}
    >
      {isError || isEmpty ? (
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "20px",
            color: (theme) => theme.colors.text[1],
          }}
        >
          {/* A failed request is not the same as an empty period — say so */}
          {isError
            ? t("pages.dataAnalytics.internalUsage.countryUsage.loadError")
            : t("pages.dataAnalytics.internalUsage.countryUsage.noData")}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {visibleRows.map((row) => (
            <CountryUsageRow
              barColor={barColor}
              isUserLevelDataEnabled={isUserLevelDataEnabled}
              key={row.isOther ? "__other__" : row.name}
              row={row}
            />
          ))}
          {canExpand && (
            <Box
              component="button"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                alignSelf: "flex-end",
                background: "none",
                border: "none",
                color: (theme) => theme.colors.text[5],
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "0.02em",
                lineHeight: "20px",
                mt: 0.5,
                padding: 0,
                textDecoration: "underline",
                textDecorationColor: "currentColor",
              }}
            >
              {expanded
                ? t("pages.dataAnalytics.internalUsage.countryUsage.showLess")
                : t("pages.dataAnalytics.internalUsage.countryUsage.showMore")}
            </Box>
          )}
        </Box>
      )}
    </ContainerCard>
  );
};
